'use client';

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  ZoomIn,
  Maximize2,
  Minimize2,
  Download,
  Loader2,
  BookOpen,
  HelpCircle
} from 'lucide-react';

interface PDFViewport {
  width: number;
  height: number;
}

interface PDFPageProxy {
  getViewport: (options: { scale: number }) => PDFViewport;
  render: (options: { canvasContext: CanvasRenderingContext2D; viewport: PDFViewport }) => {
    promise: Promise<void>;
  };
}

interface PDFDocumentProxy {
  numPages: number;
  getPage: (pageNumber: number) => Promise<PDFPageProxy>;
  destroy: () => void;
}

declare global {
  interface Window {
    pdfjsLib: {
      GlobalWorkerOptions: {
        workerSrc: string;
      };
      getDocument: (options: {
        url: string;
        disableRange?: boolean;
        disableStream?: boolean;
      }) => {
        promise: Promise<PDFDocumentProxy>;
        destroy: () => void;
      };
    };
  }
}

interface BookViewerProps {
  pdfUrl: string;
  downloadUrl?: string;
  fileName?: string;
}

interface TransitionState {
  direction: 'next' | 'prev';
  pageBefore: number;
}

export default function BookViewer({
  pdfUrl,
  downloadUrl = '/catalog/katalog.pdf',
  fileName = 'filiz-dekorasyon-katalog.pdf'
}: BookViewerProps) {
  const [pdfjsLoaded, setPdfjsLoaded] = useState(false);
  const [pdfDoc, setPdfDoc] = useState<PDFDocumentProxy | null>(null);
  const [numPages, setNumPages] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [zoom, setZoom] = useState<number>(1.0);
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Layout sizing
  const [aspectRatio, setAspectRatio] = useState<number>(1.414); // A4 ratio default
  const [pageSize, setPageSize] = useState<{ width: number; height: number }>({ width: 320, height: 450 });

  // Cache for rendered page images (as data URLs)
  const renderedPagesRef = useRef<Record<number, string>>({});
  const [renderedPages, setRenderedPages] = useState<Record<number, string>>({});

  // 3D transition state
  const [transition, setTransition] = useState<TransitionState | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);
  const viewerRef = useRef<HTMLDivElement>(null);

  // Load PDF.js from CDN
  useEffect(() => {
    if (window.pdfjsLib) {
      setPdfjsLoaded(true);
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
    script.async = true;

    script.onload = () => {
      window.pdfjsLib.GlobalWorkerOptions.workerSrc =
        'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
      setPdfjsLoaded(true);
    };

    script.onerror = () => {
      setError('PDF görüntüleyici yüklenemedi. Lütfen internet bağlantınızı kontrol edip sayfayı yenileyin.');
      setLoading(false);
    };

    document.head.appendChild(script);
  }, []);

  // Handle responsiveness
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Monitor fullscreen change
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // Load PDF Document
  useEffect(() => {
    if (!pdfjsLoaded) return;

    setLoading(true);
    setError(null);

    const loadingTask = window.pdfjsLib.getDocument({
      url: pdfUrl,
      disableRange: false,
      disableStream: false,
    });

    loadingTask.promise
      .then((pdf: PDFDocumentProxy) => {
        setPdfDoc(pdf);
        setNumPages(pdf.numPages);
        setLoading(false);
      })
      .catch((err: Error) => {
        console.error('Error loading PDF:', err);
        setError('Katalog yüklenirken bir hata oluştu. Lütfen daha sonra tekrar deneyin.');
        setLoading(false);
      });

    return () => {
      if (loadingTask) {
        loadingTask.destroy();
      }
    };
  }, [pdfjsLoaded, pdfUrl]);

  // Read page aspect ratio once document is loaded
  useEffect(() => {
    if (!pdfDoc) return;
    pdfDoc.getPage(1)
      .then((page) => {
        const viewport = page.getViewport({ scale: 1.0 });
        setAspectRatio(viewport.height / viewport.width);
      })
      .catch((err) => {
        console.error('Error reading PDF aspect ratio:', err);
      });
  }, [pdfDoc]);

  // Helper to render a specific page to a data URL
  const renderPageToDataUrl = useCallback(
    async (pageNum: number): Promise<string | null> => {
      if (!pdfDoc) return null;
      try {
        const page = await pdfDoc.getPage(pageNum);
        const unscaledViewport = page.getViewport({ scale: 1.0 });

        // Standard high-quality width for rendering (approx 900px wide for crisp text)
        const targetWidth = 900;
        const scale = targetWidth / unscaledViewport.width;
        const viewport = page.getViewport({ scale });

        const canvas = document.createElement('canvas');
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        const context = canvas.getContext('2d');
        if (!context) return null;

        await page.render({
          canvasContext: context,
          viewport: viewport,
        }).promise;

        return canvas.toDataURL('image/jpeg', 0.85);
      } catch (err: unknown) {
        const error = err as { name?: string };
        if (error.name !== 'RenderingCancelledException') {
          console.error(`Error rendering page ${pageNum} to data URL:`, err);
        }
        return null;
      }
    },
    [pdfDoc]
  );

  // Clear cache when pdfUrl changes
  useEffect(() => {
    setRenderedPages({});
    renderedPagesRef.current = {};
  }, [pdfUrl]);

  // Outward pre-rendering / caching logic (priority-queue)
  useEffect(() => {
    if (!pdfDoc) return;

    let active = true;
    const renderQueue: number[] = [];

    // Sort all page numbers by distance to current page
    const pageDistances = Array.from({ length: numPages }, (_, i) => i + 1)
      .map((pageNum) => ({ pageNum, dist: Math.abs(pageNum - currentPage) }))
      .sort((a, b) => a.dist - b.dist);

    pageDistances.forEach((item) => {
      if (!renderedPagesRef.current[item.pageNum]) {
        renderQueue.push(item.pageNum);
      }
    });

    const processQueue = async () => {
      if (renderQueue.length === 0 || !active) return;
      const pageNum = renderQueue.shift()!;

      const dataUrl = await renderPageToDataUrl(pageNum);
      if (dataUrl && active) {
        renderedPagesRef.current[pageNum] = dataUrl;
        setRenderedPages((prev) => ({
          ...prev,
          [pageNum]: dataUrl,
        }));
        // process next after short tick
        setTimeout(processQueue, 50);
      } else {
        setTimeout(processQueue, 50);
      }
    };

    processQueue();

    return () => {
      active = false;
    };
  }, [pdfDoc, currentPage, numPages, renderPageToDataUrl]);

  // Update layout page sizes based on container width, viewport height, and aspect ratio
  const updatePageSize = useCallback(() => {
    if (!viewerRef.current || !pdfDoc) return;

    const container = viewerRef.current;
    
    // Set max height dynamically to ensure everything fits inside the device screen
    const maxViewerHeight = isMobile
      ? (isFullscreen ? window.innerHeight - 150 : 380)
      : (isFullscreen ? window.innerHeight - 180 : 520);
      
    const maxViewerWidth = container.clientWidth;

    // Available width for a single page
    const availableWidth = isMobile ? maxViewerWidth - 32 : (maxViewerWidth - 64) / 2;

    let height = maxViewerHeight;
    let width = height / aspectRatio;

    if (width > availableWidth) {
      width = availableWidth;
      height = width * aspectRatio;
    }

    setPageSize({
      width: width * zoom,
      height: height * zoom
    });
  }, [pdfDoc, isMobile, zoom, isFullscreen, aspectRatio]);

  useEffect(() => {
    updatePageSize();
    window.addEventListener('resize', updatePageSize);
    return () => window.removeEventListener('resize', updatePageSize);
  }, [updatePageSize]);

  // Double page spread assignments
  const leftPageNum = currentPage % 2 === 0 ? currentPage : currentPage - 1;
  const rightPageNum = leftPageNum + 1 <= numPages ? leftPageNum + 1 : null;

  const getNextPageNumber = useCallback(() => {
    if (currentPage === 1) return 2;
    const nextLeft = leftPageNum + 2;
    return nextLeft <= numPages ? nextLeft : currentPage;
  }, [currentPage, leftPageNum, numPages]);

  const getPrevPageNumber = useCallback(() => {
    if (currentPage <= 3) return 1;
    const prevLeft = leftPageNum - 2;
    return Math.max(1, prevLeft);
  }, [currentPage, leftPageNum]);

  // Page turn handlers
  const handleNext = useCallback(() => {
    if (currentPage >= numPages || transition) return;

    if (isMobile) {
      setCurrentPage((prev) => Math.min(numPages, prev + 1));
      return;
    }

    const nextPage = getNextPageNumber();
    if (nextPage === currentPage) return;

    // Pre-verify that the required pages are in cache, otherwise fallback to standard page jump
    const oldRight = rightPageNum;
    const newLeft = nextPage;
    const newRight = nextPage + 1 <= numPages ? nextPage + 1 : null;

    if (
      (oldRight && !renderedPages[oldRight]) ||
      !renderedPages[newLeft] ||
      (newRight && !renderedPages[newRight])
    ) {
      setCurrentPage(nextPage);
      return;
    }

    setTransition({
      direction: 'next',
      pageBefore: currentPage
    });
  }, [currentPage, numPages, isMobile, rightPageNum, renderedPages, getNextPageNumber, transition]);

  const handlePrev = useCallback(() => {
    if (currentPage <= 1 || transition) return;

    if (isMobile) {
      setCurrentPage((prev) => Math.max(1, prev - 1));
      return;
    }

    const prevPage = getPrevPageNumber();
    if (prevPage === currentPage) return;

    const oldLeft = leftPageNum;
    const newLeft = prevPage % 2 === 0 ? prevPage : prevPage - 1;
    const newRight = newLeft + 1 <= numPages ? newLeft + 1 : null;

    if (
      (newRight && !renderedPages[newRight]) ||
      !renderedPages[oldLeft] ||
      (newLeft > 1 && !renderedPages[newLeft])
    ) {
      setCurrentPage(prevPage);
      return;
    }

    setTransition({
      direction: 'prev',
      pageBefore: currentPage
    });
  }, [currentPage, isMobile, leftPageNum, renderedPages, getPrevPageNumber, transition]);

  const handleFirst = () => {
    if (transition) return;
    setCurrentPage(1);
  };

  const handleLast = () => {
    if (transition) return;
    if (isMobile) {
      setCurrentPage(numPages);
    } else {
      setCurrentPage(numPages % 2 === 0 ? numPages : numPages - 1);
    }
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') handleNext();
      if (e.key === 'ArrowLeft') handlePrev();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleNext, handlePrev]);

  // Zoom handlers
  const zoomIn = () => setZoom((z) => Math.min(2.5, z + 0.25));
  const zoomOut = () => setZoom((z) => Math.max(0.5, z - 0.25));
  const resetZoom = () => setZoom(1.0);

  // Fullscreen handler
  const toggleFullscreen = () => {
    if (!containerRef.current) return;

    if (!document.fullscreenElement) {
      containerRef.current.requestFullscreen().catch((err) => {
        console.error('Error entering fullscreen:', err);
      });
    } else {
      document.exitFullscreen();
    }
  };

  // Slider change
  const handleSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (transition) return;
    const pageNum = parseInt(e.target.value, 10);
    if (pageNum >= 1 && pageNum <= numPages) {
      if (isMobile) {
        setCurrentPage(pageNum);
      } else {
        if (pageNum === 1) {
          setCurrentPage(1);
        } else {
          setCurrentPage(pageNum % 2 === 0 ? pageNum : pageNum - 1);
        }
      }
    }
  };

  // Dragging / Swiping on mobile
  const dragStartX = useRef(0);
  const handleTouchStart = (e: React.TouchEvent) => {
    dragStartX.current = e.touches[0].clientX;
  };
  const handleTouchEnd = (e: React.TouchEvent) => {
    const dragEndX = e.changedTouches[0].clientX;
    const diff = dragStartX.current - dragEndX;
    if (Math.abs(diff) > 50) {
      if (diff > 0) {
        handleNext();
      } else {
        handlePrev();
      }
    }
  };

  // Visual stack effect count
  const leftStackCount = currentPage === 1 ? 0 : Math.min(5, Math.ceil(leftPageNum / 10));
  const rightStackCount = currentPage === 1
    ? Math.min(5, Math.ceil(numPages / 10))
    : Math.min(5, Math.ceil((numPages - (rightPageNum || leftPageNum)) / 10));

  return (
    <div
      ref={containerRef}
      className={`relative flex flex-col justify-between w-full overflow-hidden bg-slate-950 text-white transition-all select-none ${
        isFullscreen ? 'h-screen p-4' : 'rounded-2xl border border-slate-800/80 shadow-2xl h-[540px] md:h-[680px]'
      }`}
    >
      {/* Background decoration */}
      <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-slate-900 via-slate-950 to-black opacity-80" />

      {/* Top Header Controls */}
      <div className="relative z-10 flex items-center justify-between px-6 py-4 border-b bg-slate-950/70 backdrop-blur-md border-slate-900">
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-primary/10 text-primary">
            <BookOpen className="w-5 h-5" />
          </div>
          <div>
            <h4 className="text-sm font-semibold tracking-wide text-slate-200 uppercase">
              DİJİTAL KATALOG
            </h4>
            <p className="text-xs text-slate-400">
              {pdfDoc ? `${numPages} Sayfa` : 'Yükleniyor...'}
            </p>
          </div>
        </div>

        {/* Toolbar Controls */}
        <div className="flex items-center gap-2">
          {/* Zoom Actions - Hidden on mobile to prevent layout overflow */}
          <div className="hidden md:flex items-center gap-1 bg-slate-900/60 rounded-lg p-1 border border-slate-800/40">
            <button
              onClick={zoomOut}
              className="p-1.5 hover:bg-slate-800 rounded-md text-slate-400 hover:text-white transition-colors"
              title="Uzaklaştır"
              type="button"
            >
              <ZoomIn className="w-4 h-4 transform rotate-90 scale-x-[-1]" />
            </button>
            <span className="text-xs font-mono text-slate-300 w-12 text-center">
              %{Math.round(zoom * 100)}
            </span>
            <button
              onClick={zoomIn}
              className="p-1.5 hover:bg-slate-800 rounded-md text-slate-400 hover:text-white transition-colors"
              title="Yakınlaştır"
              type="button"
            >
              <ZoomIn className="w-4 h-4" />
            </button>
            <button
              onClick={resetZoom}
              className="px-2 py-1 text-[10px] bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white rounded transition-colors"
              title="Sığdır"
              type="button"
            >
              Sığdır
            </button>
          </div>

          {/* View Actions */}
          <button
            onClick={toggleFullscreen}
            className="p-2 hover:bg-slate-900 hover:text-white text-slate-400 rounded-lg border border-slate-800/40 transition-colors bg-slate-900/40"
            title={isFullscreen ? 'Tam Ekrandan Çık' : 'Tam Ekran Yap'}
            type="button"
          >
            {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
          </button>

          <a
            href={downloadUrl}
            download={fileName}
            className="p-2 hover:bg-primary/20 hover:text-primary-light text-slate-400 rounded-lg border border-slate-800/40 transition-colors bg-slate-900/40 flex items-center gap-2"
            title="PDF İndir"
          >
            <Download className="w-4 h-4" />
            <span className="hidden md:inline text-xs font-medium">İndir</span>
          </a>
        </div>
      </div>

      {/* Main Book Area */}
      <div
        ref={viewerRef}
        className="relative z-10 flex-grow flex items-center justify-center overflow-auto px-4 md:px-12 py-6 min-h-0"
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        {loading && (
          <div className="flex flex-col items-center gap-4 text-center">
            <Loader2 className="w-12 h-12 text-primary animate-spin" />
            <div>
              <p className="text-base font-medium text-slate-200">Katalog Yükleniyor...</p>
              <p className="text-xs text-slate-400 mt-1">Dosya boyutu büyük olduğu için birkaç saniye sürebilir.</p>
            </div>
          </div>
        )}

        {error && (
          <div className="flex flex-col items-center gap-3 text-center max-w-md p-6 bg-red-950/20 border border-red-900/50 rounded-xl">
            <HelpCircle className="w-10 h-10 text-red-500" />
            <p className="text-sm text-red-200">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="mt-2 px-4 py-2 bg-red-900 hover:bg-red-800 text-white rounded-lg text-xs font-medium transition-colors"
              type="button"
            >
              Yeniden Dene
            </button>
          </div>
        )}

        {!loading && !error && pdfDoc && (
          <div className="relative flex items-center justify-center max-w-full max-h-full">
            
            {/* Desktop Double-Page View */}
            {!isMobile && (
              <div className="flex items-center justify-center relative select-none" style={{ perspective: '2000px' }}>
                
                {/* Left Stack Pages (visual thickness effect) */}
                {currentPage > 1 && Array.from({ length: leftStackCount }).map((_, i) => (
                  <div
                    key={`lstack-${i}`}
                    className="absolute bg-white border-l border-t border-b border-slate-200/80 rounded-l shadow-sm"
                    style={{
                      height: `${pageSize.height}px`,
                      width: `${pageSize.width}px`,
                      transform: `translateX(-${(i + 1) * 2}px) translateZ(-${(i + 1) * 2}px) scaleX(0.998)`,
                      transformOrigin: 'right center',
                      right: '50%',
                      opacity: 0.8 - (i * 0.1),
                      zIndex: 5
                    }}
                  />
                ))}

                {/* Right Stack Pages (visual thickness effect) */}
                {Array.from({ length: rightStackCount }).map((_, i) => (
                  <div
                    key={`rstack-${i}`}
                    className="absolute bg-white border-r border-t border-b border-slate-200/80 rounded-r shadow-sm"
                    style={{
                      height: `${pageSize.height}px`,
                      width: `${pageSize.width}px`,
                      transform: `translateX(${(i + 1) * 2}px) translateZ(-${(i + 1) * 2}px) scaleX(0.998)`,
                      transformOrigin: 'left center',
                      left: '50%',
                      opacity: 0.8 - (i * 0.1),
                      zIndex: 5
                    }}
                  />
                ))}

                {/* Static Spread / Active Transition */}
                <div
                  className="flex items-center justify-center shadow-[0_20px_50px_rgba(0,0,0,0.5)] rounded-lg relative bg-slate-900 border border-slate-800"
                  style={{
                    width: `${pageSize.width * 2}px`,
                    height: `${pageSize.height}px`,
                    transformStyle: 'preserve-3d',
                    zIndex: 10
                  }}
                >
                  {/* 1. Left Page (Static Underneath) */}
                  <div
                    style={{
                      position: 'absolute',
                      left: 0,
                      top: 0,
                      width: `${pageSize.width}px`,
                      height: `${pageSize.height}px`,
                      backgroundColor: currentPage === 1 ? 'transparent' : '#ffffff',
                      zIndex: 10,
                      overflow: 'hidden',
                      display: currentPage === 1 ? 'none' : 'block'
                    }}
                  >
                    {(() => {
                      let pageToRender = leftPageNum;
                      if (transition) {
                        if (transition.direction === 'next') {
                          pageToRender = leftPageNum;
                        } else {
                          const prevLeft = getPrevPageNumber();
                          pageToRender = prevLeft % 2 === 0 ? prevLeft : prevLeft - 1;
                        }
                      }

                      if (pageToRender <= 1) return null;

                      const imgUrl = renderedPages[pageToRender];
                      if (!imgUrl) {
                        return (
                          <div className="w-full h-full bg-white flex flex-col items-center justify-center gap-2">
                            <Loader2 className="w-6 h-6 text-primary animate-spin" />
                          </div>
                        );
                      }
                      return (
                        <>
                          <img src={imgUrl} alt={`Page ${pageToRender}`} className="w-full h-full object-contain select-none" />
                          {/* Spine crease shadow */}
                          <div className="absolute right-0 top-0 bottom-0 w-12 bg-gradient-to-r from-transparent to-black/15 pointer-events-none z-20" />
                          {/* Page edge highlight */}
                          <div className="absolute left-0 top-0 bottom-0 w-2 bg-gradient-to-r from-white/10 to-transparent pointer-events-none z-20" />
                        </>
                      );
                    })()}
                  </div>

                  {/* 2. Right Page (Static Underneath) */}
                  <div
                    style={{
                      position: 'absolute',
                      right: 0,
                      top: 0,
                      width: `${pageSize.width}px`,
                      height: `${pageSize.height}px`,
                      backgroundColor: '#ffffff',
                      zIndex: 10,
                      overflow: 'hidden'
                    }}
                  >
                    {(() => {
                      let pageToRender = rightPageNum || 1;
                      if (transition) {
                        if (transition.direction === 'next') {
                          const nextLeft = getNextPageNumber();
                          pageToRender = nextLeft + 1 <= numPages ? nextLeft + 1 : 1;
                        } else {
                          pageToRender = rightPageNum || 1;
                        }
                      }

                      if (pageToRender > numPages) {
                        return <div className="w-full h-full bg-white" />;
                      }

                      const imgUrl = renderedPages[pageToRender];
                      if (!imgUrl) {
                        return (
                          <div className="w-full h-full bg-white flex flex-col items-center justify-center gap-2">
                            <Loader2 className="w-6 h-6 text-primary animate-spin" />
                          </div>
                        );
                      }
                      return (
                        <>
                          <img src={imgUrl} alt={`Page ${pageToRender}`} className="w-full h-full object-contain select-none" />
                          {/* Spine crease shadow */}
                          <div className="absolute left-0 top-0 bottom-0 w-12 bg-gradient-to-l from-transparent to-black/15 pointer-events-none z-20" />
                          {/* Page edge highlight */}
                          <div className="absolute right-0 top-0 bottom-0 w-2 bg-gradient-to-l from-white/10 to-transparent pointer-events-none z-20" />
                        </>
                      );
                    })()}
                  </div>

                  {/* 3. 3D Flipping Page */}
                  {transition && (
                    <motion.div
                      key={`${transition.pageBefore}-${transition.direction}`}
                      initial={{ rotateY: transition.direction === 'next' ? 0 : -180 }}
                      animate={{ rotateY: transition.direction === 'next' ? -180 : 0 }}
                      transition={{ duration: 0.6, ease: [0.25, 1, 0.5, 1] }}
                      onAnimationComplete={() => {
                        setCurrentPage(transition.direction === 'next' ? getNextPageNumber() : getPrevPageNumber());
                        setTransition(null);
                      }}
                      className="absolute top-0"
                      style={{
                        left: transition.direction === 'next' ? `${pageSize.width}px` : 0,
                        width: `${pageSize.width}px`,
                        height: `${pageSize.height}px`,
                        transformOrigin: transition.direction === 'next' ? 'left center' : 'right center',
                        transformStyle: 'preserve-3d',
                        zIndex: 40
                      }}
                    >
                      {/* Front Face */}
                      <div
                        className="absolute inset-0 bg-white"
                        style={{
                          backfaceVisibility: 'hidden',
                          transform: 'rotateY(0deg)',
                          overflow: 'hidden'
                        }}
                      >
                        {(() => {
                          let pageToRender = rightPageNum || 1;
                          if (transition.direction === 'prev') {
                            const prevLeft = getPrevPageNumber();
                            pageToRender = prevLeft + 1 <= numPages ? prevLeft + 1 : 1;
                          }

                          const imgUrl = renderedPages[pageToRender];
                          return imgUrl ? (
                            <>
                              <img src={imgUrl} alt="Flipping Front" className="w-full h-full object-contain select-none" />
                              <div className="absolute inset-y-0 w-8 bg-gradient-to-r from-black/5 to-transparent pointer-events-none" style={{ left: transition.direction === 'next' ? 0 : 'auto', right: transition.direction === 'prev' ? 0 : 'auto' }} />
                            </>
                          ) : (
                            <div className="w-full h-full bg-white" />
                          );
                        })()}
                      </div>

                      {/* Back Face */}
                      <div
                        className="absolute inset-0 bg-white"
                        style={{
                          backfaceVisibility: 'hidden',
                          transform: 'rotateY(180deg)',
                          overflow: 'hidden'
                        }}
                      >
                        {(() => {
                          let pageToRender = currentPage;
                          if (transition.direction === 'next') {
                            const nextLeft = getNextPageNumber();
                            pageToRender = nextLeft;
                          } else {
                            pageToRender = leftPageNum;
                          }

                          const imgUrl = renderedPages[pageToRender];
                          return imgUrl ? (
                            <>
                              <img src={imgUrl} alt="Flipping Back" className="w-full h-full object-contain select-none" />
                              <div className="absolute inset-y-0 w-8 bg-gradient-to-l from-black/5 to-transparent pointer-events-none" style={{ right: transition.direction === 'next' ? 0 : 'auto', left: transition.direction === 'prev' ? 0 : 'auto' }} />
                            </>
                          ) : (
                            <div className="w-full h-full bg-white" />
                          );
                        })()}
                      </div>
                    </motion.div>
                  )}

                  {/* 4. Center Crease Line */}
                  {!(currentPage === 1 && !transition) && (
                    <div className="absolute top-0 bottom-0 left-1/2 w-[2px] bg-black/35 transform -translate-x-1/2 z-30 pointer-events-none shadow-[0_0_8px_rgba(0,0,0,0.5)]" />
                  )}
                </div>
              </div>
            )}

            {/* Mobile Single-Page View */}
            {isMobile && (
              <div className="flex items-center justify-center relative select-none">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={currentPage}
                    initial={{ opacity: 0, x: 30 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -30 }}
                    transition={{ duration: 0.25, ease: 'easeInOut' }}
                    className="relative bg-white shadow-2xl rounded-lg overflow-hidden border border-slate-200"
                    style={{
                      width: `${pageSize.width}px`,
                      height: `${pageSize.height}px`
                    }}
                  >
                    {!renderedPages[currentPage] ? (
                      <div className="w-full h-full bg-white flex flex-col items-center justify-center gap-2">
                        <Loader2 className="w-6 h-6 text-primary animate-spin" />
                        <span className="text-xs text-slate-400">Yükleniyor...</span>
                      </div>
                    ) : (
                      <>
                        <img
                          src={renderedPages[currentPage]}
                          alt={`Page ${currentPage}`}
                          className="w-full h-full object-contain select-none"
                        />
                        <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/5 to-white/10 pointer-events-none z-20" />
                      </>
                    )}
                  </motion.div>
                </AnimatePresence>
              </div>
            )}

            {/* Next / Prev Side Buttons - Hidden on mobile to prevent layout overflow */}
            <button
              onClick={handlePrev}
              disabled={currentPage <= 1 || !!transition}
              className="absolute left-[-24px] md:left-[-60px] top-1/2 transform -translate-y-1/2 hidden md:flex items-center justify-center w-12 h-12 rounded-full bg-slate-900/60 hover:bg-primary border border-slate-800/40 hover:border-primary-light text-slate-300 hover:text-white transition-all disabled:opacity-0 disabled:pointer-events-none z-30 group shadow-lg"
              title="Önceki Sayfa"
              type="button"
            >
              <ChevronLeft className="w-6 h-6 transform group-hover:-translate-x-0.5 transition-transform" />
            </button>
            <button
              onClick={handleNext}
              disabled={currentPage >= numPages || !!transition}
              className="absolute right-[-24px] md:right-[-60px] top-1/2 transform -translate-y-1/2 hidden md:flex items-center justify-center w-12 h-12 rounded-full bg-slate-900/60 hover:bg-primary border border-slate-800/40 hover:border-primary-light text-slate-300 hover:text-white transition-all disabled:opacity-0 disabled:pointer-events-none z-30 group shadow-lg"
              title="Sonraki Sayfa"
              type="button"
            >
              <ChevronRight className="w-6 h-6 transform group-hover:translate-x-0.5 transition-transform" />
            </button>
          </div>
        )}
      </div>

      {/* Bottom Navigation Toolbar */}
      <div className="relative z-10 flex flex-col md:flex-row items-center justify-between px-6 py-4 gap-4 md:gap-0 border-t bg-slate-950/70 backdrop-blur-md border-slate-900">
        
        {/* Book Navigation controls */}
        <div className="flex items-center gap-1.5 order-2 md:order-1">
          <button
            onClick={handleFirst}
            disabled={currentPage <= 1 || !!transition}
            className="p-2 hover:bg-slate-900 rounded-lg text-slate-400 hover:text-white transition-colors disabled:opacity-20 disabled:pointer-events-none"
            title="İlk Sayfa"
            type="button"
          >
            <ChevronsLeft className="w-4 h-4" />
          </button>
          <button
            onClick={handlePrev}
            disabled={currentPage <= 1 || !!transition}
            className="p-2 hover:bg-slate-900 rounded-lg text-slate-400 hover:text-white transition-colors disabled:opacity-20 disabled:pointer-events-none flex items-center gap-1"
            title="Önceki Sayfa"
            type="button"
          >
            <ChevronLeft className="w-4 h-4" />
            <span className="text-xs hidden md:inline">Önceki</span>
          </button>

          {/* Quick Page Jump Input */}
          <div className="flex items-center gap-1 text-slate-300 text-xs px-2">
            <span className="text-slate-500 font-mono">Sayfa:</span>
            <input
              type="number"
              min={1}
              max={numPages}
              value={currentPage}
              onChange={handleSliderChange}
              disabled={!!transition}
              className="w-12 h-7 bg-slate-900 border border-slate-800 rounded text-center text-white focus:outline-none focus:border-primary text-xs font-mono [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none disabled:opacity-50"
            />
            <span className="text-slate-500">/</span>
            <span className="font-mono text-slate-400">{numPages}</span>
          </div>

          <button
            onClick={handleNext}
            disabled={currentPage >= numPages || !!transition}
            className="p-2 hover:bg-slate-900 rounded-lg text-slate-400 hover:text-white transition-colors disabled:opacity-20 disabled:pointer-events-none flex items-center gap-1"
            title="Sonraki Sayfa"
            type="button"
          >
            <span className="text-xs hidden md:inline">Sonraki</span>
            <ChevronRight className="w-4 h-4" />
          </button>
          <button
            onClick={handleLast}
            disabled={currentPage >= numPages || !!transition}
            className="p-2 hover:bg-slate-900 rounded-lg text-slate-400 hover:text-white transition-colors disabled:opacity-20 disabled:pointer-events-none"
            title="Son Sayfa"
            type="button"
          >
            <ChevronsRight className="w-4 h-4" />
          </button>
        </div>

        {/* Quick navigation slider */}
        <div className="flex items-center gap-3 w-full md:w-60 lg:w-80 order-1 md:order-2">
          <input
            type="range"
            min="1"
            max={numPages}
            value={currentPage}
            onChange={handleSliderChange}
            disabled={numPages <= 1 || !!transition}
            className="w-full h-1 bg-slate-800 rounded-lg appearance-none cursor-pointer accent-primary hover:accent-primary-light disabled:opacity-30 disabled:cursor-not-allowed"
          />
        </div>
      </div>
    </div>
  );
}
